import messages from './messages';
import { isPhaseOneHold, hasPendingPhaseOneResult } from './hooks';
import { isPhasedTransUnionProfile } from './screeningProviderUtils';

const OPEN_SCREENING_STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'PENDING'];

const enabled = { disabled: false, reason: null };
const disabled = (reason) => ({ disabled: true, reason });

function getTransUnionRequests(householdScreening) {
  return (householdScreening.requests ?? []).filter(
    (request) =>
      request?.providerPropertyProfile?.provider?.name === 'TransUnion',
  );
}

// A property is phased when a profile has the phased flag. A TransUnion hold
// at phase 1 also counts, because TransUnion can start it while the flag is off.
function isPhasedProperty({
  householdScreening,
  activeProviderPropertyProfiles,
}) {
  const hasPhasedFlag = activeProviderPropertyProfiles.some(
    (profile) =>
      profile?.provider?.name === 'TransUnion' &&
      profile?.isPhasedScreening === true,
  );
  if (hasPhasedFlag) return true;
  return getTransUnionRequests(householdScreening).some((request) =>
    isPhasedTransUnionProfile(
      request.providerPropertyProfile,
      householdScreening.phase,
    ),
  );
}

// An agent stop declines the TransUnion request at phase 1 and leaves a result
// that is not DECLINED. An automatic TransUnion decline has a DECLINED result.
function isStopped(householdScreening) {
  return (
    householdScreening.phase === 1 &&
    getTransUnionRequests(householdScreening).some(
      (request) =>
        request.status === 'DECLINED' && request.result?.status !== 'DECLINED',
    )
  );
}

function getScreeningState(householdScreening) {
  const hasFinalDecision = householdScreening.finalDecisions?.length > 0;
  if (hasFinalDecision && isStopped(householdScreening)) return 'stopped';
  if (
    hasFinalDecision ||
    !OPEN_SCREENING_STATUSES.includes(householdScreening.status)
  ) {
    return 'complete';
  }
  if (householdScreening.phase === 2) return 'criminalRequested';
  if (
    isPhaseOneHold(householdScreening) &&
    !hasPendingPhaseOneResult(householdScreening)
  ) {
    return 'decisionNeeded';
  }
  return 'waiting';
}

export function getScreeningMenuState({
  householdScreening,
  activeProviderPropertyProfiles,
  hasCriminalReportPermission,
  isApplicationCanceledOrDenied,
  isSubmitButtonDisabled,
}) {
  const isMenu =
    Boolean(householdScreening) &&
    isPhasedProperty({ householdScreening, activeProviderPropertyProfiles });
  const isDisabled = Boolean(isApplicationCanceledOrDenied);

  if (!isMenu) {
    return {
      isMenu,
      isDisabled,
      isDecisionNeeded: false,
      options: {
        resubmit: disabled(null),
        continue: disabled(null),
        stop: disabled(null),
      },
    };
  }

  const state = getScreeningState(householdScreening);
  let options;
  switch (state) {
    case 'decisionNeeded':
      options = {
        resubmit: disabled(messages.menuReasonInProgress),
        continue: hasCriminalReportPermission
          ? enabled
          : disabled(messages.menuReasonNoPermission),
        stop: hasCriminalReportPermission
          ? enabled
          : disabled(messages.menuReasonNoPermission),
      };
      break;
    case 'criminalRequested':
      options = {
        resubmit: disabled(messages.menuReasonInProgress),
        continue: disabled(messages.menuReasonCriminalRequested),
        stop: disabled(messages.menuReasonCriminalRunning),
      };
      break;
    case 'complete':
      options = {
        resubmit: enabled,
        continue: disabled(messages.menuReasonComplete),
        stop: disabled(messages.menuReasonComplete),
      };
      break;
    case 'stopped':
      options = {
        resubmit: enabled,
        continue: disabled(messages.menuReasonStopped),
        stop: disabled(messages.menuReasonStopped),
      };
      break;
    default:
      options = {
        resubmit: disabled(messages.menuReasonInProgress),
        continue: disabled(messages.menuReasonWaitingForTransUnion),
        stop: disabled(messages.menuReasonWaitingForTransUnion),
      };
  }

  // The plain button rules also apply to Resubmit. The tooltip text comes
  // from useTooltipText, so the caller reads it instead of a message here.
  if (isSubmitButtonDisabled) {
    options.resubmit = disabled('submitTooltip');
  }

  return {
    isMenu,
    isDisabled,
    isDecisionNeeded: state === 'decisionNeeded',
    options,
  };
}
